import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Palette, RotateCcw, Save } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { NewActivityButton } from "./NewActivity";

const GLOBALS = [["header", "En-tête du tableau"], ["grid", "Quadrillage"], ["weekend", "Week-end"], ["sunday_border", "Liseré dimanche / férié"], ["holiday_line", "Liseré jour férié"]];

const ColorInput = ({ id, value, onChange }) => (
  <input data-testid={id} type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-8 w-12 rounded border border-slate-300 cursor-pointer bg-white" />
);

function Preview({ s, acts }) {
  const days = ["L 01", "M 02", "M 03 férié", "S 06", "D 07"];
  return (
    <table className="text-[10px] border-collapse w-full max-w-md" style={{ borderColor: s.grid }} data-testid="planning-style-preview">
      <thead><tr>{["Agent", ...days].map((d, i) => <th key={d} className="px-1 py-1 text-white font-bold" style={{ background: i === 2 ? s.holiday_line : i >= 3 ? "#334155" : s.header, border: `1px solid ${s.grid}` }}>{d}</th>)}</tr></thead>
      <tbody>
        {acts.slice(0, 3).map((a, r) => (
          <tr key={a.code}>
            <td className="px-1 font-bold" style={{ border: `1px solid ${s.grid}` }}>{a.label}</td>
            {days.map((d, i) => {
              const night = (r + i) % 2 === 1, show = i !== 1;
              const bg = show ? s.colors[a.code][night ? "night" : "day"] : (i >= 3 ? s.weekend : "#fff");
              const gold = show && (i === 2 || i === 4);
              return <td key={d} className="text-center px-1 py-1" style={{ background: bg, border: `1px solid ${s.grid}`, borderLeft: i === 2 ? `2px solid ${s.holiday_line}` : `1px solid ${s.grid}`, borderRight: i === 2 ? `2px solid ${s.holiday_line}` : `1px solid ${s.grid}`, outline: gold ? `2px solid ${s.sunday_border}` : "none", outlineOffset: -3 }}>
                {show && <><b>{s.prefixes[a.code]}{night ? "N" : "J"}1</b><br />{night ? "19:00" : "07:00"}</>}
              </td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export const PlanningStyleSettings = () => {
  const [d, setD] = useState(null);
  const [s, setS] = useState(null);
  useEffect(() => { http.get("/settings/planning-style").then((r) => { setD(r.data); setS(r.data.style); }); }, []);
  const added = () => http.get("/settings/planning-style").then((r) => {
    setD(r.data);
    setS((x) => ({ ...r.data.style, ...x, prefixes: { ...r.data.style.prefixes, ...x.prefixes }, colors: { ...r.data.style.colors, ...x.colors } }));
  });
  if (!s) return null;
  const setColor = (code, t, v) => setS({ ...s, colors: { ...s.colors, [code]: { ...s.colors[code], [t]: v } } });
  const save = async () => { try { const r = await http.put("/settings/planning-style", s); setS(r.data.style); toast.success("Style du planning enregistré"); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="border border-slate-200 bg-white rounded-md p-6" data-testid="planning-style-settings">
      <div className="flex items-center gap-2 mb-1"><Palette className="h-5 w-5 text-sky-600" /><h2 className="font-display text-xl font-bold">Planning PDF : codes et couleurs</h2></div>
      <p className="text-sm text-slate-600 mb-4">Préfixe de code par activité (ex. S1 → S1J1 / S1N1) et couleurs des cases jour / nuit.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <table className="text-sm w-full">
            <thead><tr className="text-[10px] uppercase tracking-widest text-slate-500 text-left"><th className="py-1">Activité</th><th>Préfixe</th><th>Jour</th><th>Nuit</th></tr></thead>
            <tbody>
              {d.activities.map((a) => (
                <tr key={a.code} className="border-t border-slate-100">
                  <td className="py-1.5 font-medium">{a.label}</td>
                  <td><input data-testid={`ps-prefix-${a.code}`} maxLength={3} className="input !w-16 !py-1 uppercase" value={s.prefixes[a.code]} onChange={(e) => setS({ ...s, prefixes: { ...s.prefixes, [a.code]: e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase() } })} /></td>
                  <td><ColorInput id={`ps-day-${a.code}`} value={s.colors[a.code].day} onChange={(v) => setColor(a.code, "day", v)} /></td>
                  <td><ColorInput id={`ps-night-${a.code}`} value={s.colors[a.code].night} onChange={(v) => setColor(a.code, "night", v)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <NewActivityButton testid="ps-new-activity" className="mt-2" onCreated={added} />
          <div className="grid grid-cols-2 gap-3 mt-4">
            {GLOBALS.map(([k, l]) => <label key={k} className="flex items-center gap-2 text-sm"><ColorInput id={`ps-${k}`} value={s[k]} onChange={(v) => setS({ ...s, [k]: v })} />{l}</label>)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Aperçu</div>
          <Preview s={s} acts={d.activities} />
        </div>
      </div>
      <div className="flex gap-2 mt-5">
        <button data-testid="ps-save-btn" onClick={save} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md text-white hover:opacity-90" style={{ backgroundColor: "var(--brand-primary)" }}><Save className="h-4 w-4" />Enregistrer</button>
        <button data-testid="ps-reset-btn" onClick={() => setS(d.defaults)} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-slate-300 hover:bg-slate-50"><RotateCcw className="h-4 w-4" />Couleurs par défaut</button>
      </div>
    </div>
  );
};
